import React, { useState, useRef, useEffect } from "react";
import { object, string } from "prop-types";
import { CompanyCard, PeopleCard, AlphabetButtons } from "../../molecules";
import { SvgIconHandler } from "../../atoms/SvgIconHandler";
import { STATIC_KEYWORDS } from "../../../constants";
import { trackLoadMoreClick } from "../../utils";

import "./entity-landing.m.css";

export const EntityLanding = ({ data, page, headline, placeholder, config }) => {
  const cdnImage = `https://${config?.["cdn-image"]}`;
  const { entities } = data;
  const intialCount = 15;
  const [searchQuery, setSearchQuery] = useState("");
  const [entitiesData, setEntitiesData] = useState(entities);
  const [showCount, setShowCount] = useState(intialCount);
  const [availableLetters, setAvailableLetters] = useState([]);
  const searchInputRef = useRef(null);

  const { NO_RESULT_FOUND, TRY_AGAIN } = STATIC_KEYWORDS;

  if (!entities.length) return null;

  const startsWithCaseInsensitive = (str, prefix) => str.toLowerCase().startsWith(prefix.toLowerCase());

  useEffect(() => {
    if (entities.length) {
      entities.sort((a, b) => a.name.trim().localeCompare(b.name.trim()));
      const letters = new Set(entities.map((company) => company.name.charAt(0).toUpperCase()));
      setAvailableLetters([...letters]);
      setEntitiesData(entities);
    }
  }, [entities]);

  useEffect(() => {
    let filtered = entities || [];
    if (searchQuery) {
      filtered = filtered.filter((company) => startsWithCaseInsensitive(company.name, searchQuery));
    }
    setEntitiesData(filtered);
  }, [entities, searchQuery, showCount]);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_page_info",
      cms_author_name: "NA",
      byline: "NA",
      category: page || "NA",
      sub_category: "NA",
      content_id: "NA",
      template_name: "listing",
      publish_date: "NA",
      publish_time: "NA",
      login_status: "not-logged-in", // will be changed
      subscription_status: "not-subscribed", // will be changed
      ad_free: false, // will be changed
      login_wall_enabled: false,
      platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
    });
  }, []);


  const handleLoadMore = () => {
    trackLoadMoreClick({
      linkCategory: page?.toLowerCase(),
      category: page?.toLowerCase(),
      subCategory: "NA",
      templateName: "listing",
    });
    setShowCount(showCount + intialCount);
  };

  // Track entity list interactions (companies or people)
  let isTracking = false;
  const trackEntityListInteraction = (eventName, value, position = null) => {
    if (isTracking) return;
    isTracking = true;
    setTimeout(() => {
      isTracking = false;
    }, 300);
    if (typeof window !== "undefined" && window.dataLayer) {
      // Determine event name and category based on page type
      const eventType = page === "people" ? "dl_people_list_interactions" : "dl_company_list_interactions";
      const category = page === "people" ? "people" : "companies";
      const eventData = {
        event: eventType,
        category: category,
        sub_category: "NA",
        template_name: "listing",
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      };

      // Add appropriate properties based on event name
      if (eventName === "filter_applied") {
        eventData.event_action = eventName;
        eventData.link_text = value;
      } else if (eventName === "company_search" || eventName === "people_search") {
        eventData.event_action = eventName;
        eventData.search_term = value;
      } else if (position) {
        eventData.link_text = value;
        eventData.link_position = position;
      }
      window.dataLayer.push(eventData);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const searchTerm = searchInputRef.current.value;
    setSearchQuery(searchTerm);
    setShowCount(intialCount);
    // Track search if there's a search term
    if (searchTerm) {
      const searchEventName = page === "people" ? "people_search" : "company_search";
      trackEntityListInteraction(searchEventName, searchTerm.toLowerCase());
    }
  };

  const handleAlphabetClick = (letter) => {
    const newQuery = letter === searchQuery ? null : letter;
    setSearchQuery(newQuery);
    setShowCount(intialCount);
    if (searchInputRef?.current) {
      searchInputRef.current.value = "";
    }
    // Track alphabet filter if a letter was selected
    if (newQuery) {
      trackEntityListInteraction("filter_applied", newQuery.toUpperCase());
    }
  };

  const getEntityCard = (entity, index) => {
    switch (page) {
      case "companies": {
        return <CompanyCard card={entity} cdnImage={cdnImage} index={index} />;
      }
      case "people": {
        return <PeopleCard card={entity} cdnImage={cdnImage} index={index} />;
      }
      default: {
        return <CompanyCard card={entity} />;
      }
    }
  };

  return (
    <div styleName="container">
      <div styleName="headline-and-search">
        <h1>{headline}</h1>
        <form onSubmit={handleSearch} styleName="search-container">
          <div styleName="search-wrapper">
            <input type="text" name="search" placeholder={placeholder} styleName="search-input" ref={searchInputRef} />
            {searchQuery && searchInputRef?.current?.value !== "" && (
              <span
                styleName="clear-search"
                onClick={() => {
                  setSearchQuery("");
                  searchInputRef.current.value = "";
                }}
              >
                <SvgIconHandler type="close" height="12px" width="12px" viewBox="0 0 12 12" />
              </span>
            )}
          </div>
          <button type="submit" styleName="search-button">
            {STATIC_KEYWORDS.SEARCH}
          </button>
        </form>
      </div>
      <div styleName="alphabets-div">
        <AlphabetButtons onClick={handleAlphabetClick} availableLetters={availableLetters} searchQuery={searchQuery} />
      </div>
      {entitiesData.length ? (
        <div styleName="entity-list">
          {entitiesData.slice(0, showCount).map((entity, index) => getEntityCard(entity, index))}
        </div>
      ) : (
        <div styleName="oops-text">
          {entities.length ? (
            <span>
              {NO_RESULT_FOUND}'{searchQuery}' <br /> {TRY_AGAIN}
            </span>
          ) : null}
        </div>
      )}

      {entitiesData.length > showCount ? (
        <div styleName="load-more-container">
          <button styleName="load-more-button" onClick={handleLoadMore}>
            {STATIC_KEYWORDS.LOAD_MORE}
          </button>
        </div>
      ) : null}
    </div>
  );
};

EntityLanding.propTypes = {
  data: object,
  page: string,
  placeholder: string,
  headline: string,
};
